import type IExtendedSearchQueryFilter from '@app/interfaces/dtos/IExtendedSearchQueryFilter';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { mapCountryIdentifiersToIds } from '@app/utils/country-identifiers';
import { useTracking } from '@composables/useTracking';

type TrackingProvider = ReturnType<typeof useTracking>;

type FilterChange = { type: FilterType; value: string; checked: boolean };

type FilterKey = { type: FilterType; value: string };

type FilterKeyMap = Map<string, FilterKey>;


export class TrackingService {
	private static instance: TrackingService | null = null;
	private readonly tracking: TrackingProvider = useTracking();

	public static getInstance(): TrackingService {
		if (!TrackingService.instance) {
			TrackingService.instance = new TrackingService();
		}

		return TrackingService.instance;
	}

	public trackSearchFromFilters(
		filters: IExtendedSearchQueryFilter[],
		totalResultsCount: number
	): void {
		if (!this.isClient()) return;

		const excludeTypes = new Set([FilterType.KEYWORD]);
		const trackingData = this.buildTrackingData(filters, excludeTypes);
		const searchTerms = this.buildSearchTermsFromFilters(filters);

		this.tracking.trackSearch(searchTerms, trackingData, totalResultsCount);
	}
	public async trackFilterChanges(
		newFilters: IExtendedSearchQueryFilter[] | undefined,
		oldFilters: IExtendedSearchQueryFilter[] | undefined
	): Promise<void> {
		if (!this.isClient()) return;

		const excludeTypes = new Set<FilterType>([FilterType.EDUCATION_LEVEL]);
		const changes = this.diffFilters(newFilters, oldFilters)
			.filter((change) => !excludeTypes.has(change.type));
		if (!changes.length) return;

		// eslint-disable-next-line @typescript-eslint/naming-convention
		const Action = await import('@studyportals/datalake-event-tracker').then((module) => module.Action);

		for (const change of changes) {
			const action = change.checked ? Action.CHECK : Action.UNCHECK;
			this.tracking.trackAction(action, change.type, change.value);
		}
	}

	public trackImpression(...args: Parameters<TrackingProvider['trackImpression']>): void {
		this.tracking.trackImpression(...args);
	}

	public trackClick(...args: Parameters<TrackingProvider['trackClick']>): void {
		this.tracking.trackClick(...args);
	}

	public trackCardImpression(...args: Parameters<TrackingProvider['trackCardImpression']>): void {
		this.tracking.trackCardImpression(...args);
	}

	public trackCardClick(...args: Parameters<TrackingProvider['trackCardClick']>): void {
		this.tracking.trackCardClick(...args);
	}

	public trackUniversityCardClick(...args: Parameters<TrackingProvider['trackUniversityCardClick']>): void {
		this.tracking.trackUniversityCardClick(...args);
	}

	public trackUniversityCardImpression(...args: Parameters<TrackingProvider['trackUniversityCardImpression']>): void {
		this.tracking.trackUniversityCardImpression(...args);
	}

	private isClient(): boolean {
		return import.meta.client;
	}

	private normalizeFilterValues(filter: IExtendedSearchQueryFilter): string[] {
		const values = filter.values.map((value) => value.id);
		if (filter.type !== FilterType.COUNTRY) return values;

		return mapCountryIdentifiersToIds(values).identifiers;
	}

	private buildFilterKeyMap(filters?: IExtendedSearchQueryFilter[]): FilterKeyMap {
		const map: FilterKeyMap = new Map();
		if (!filters) return map;

		for (const filter of filters) {
			const normalizedValues = this.normalizeFilterValues(filter);
			for (const value of normalizedValues) {
				const key = `${filter.type}:${value}`;
				map.set(key, { type: filter.type, value });
			}
		}

		return map;
	}

	private diffFilters(
		newFilters: IExtendedSearchQueryFilter[] | undefined,
		oldFilters: IExtendedSearchQueryFilter[] | undefined
	): FilterChange[] {
		const changes: FilterChange[] = [];
		const newMap = this.buildFilterKeyMap(newFilters);
		const oldMap = this.buildFilterKeyMap(oldFilters);

		for (const [key, entry] of newMap) {
			if (!oldMap.has(key)) changes.push({ ...entry, checked: true });
		}

		for (const [key, entry] of oldMap) {
			if (!newMap.has(key)) changes.push({ ...entry, checked: false });
		}

		return changes;
	}

	private buildTrackingData(
		filters: IExtendedSearchQueryFilter[],
		excludeTypes: Set<FilterType>
	): Record<string, string> {
		const trackingData = {} as Record<string, string>;

		filters
			.filter((filterSelection) => !excludeTypes.has(filterSelection.type))
			.forEach((filterSelection) => {
				const normalizedValues = this.normalizeFilterValues(filterSelection);
				trackingData[filterSelection.type] = normalizedValues.join(',');
			});

		return trackingData;
	}

	private buildSearchTermsFromFilters(
		filters: IExtendedSearchQueryFilter[],
	): string[] {
		const keywordFilter = filters.find((filter) => filter.type === FilterType.KEYWORD);
		if (!keywordFilter) return [];

		return keywordFilter.values
			.filter((value) => !!value.name)
			.map((value) => value.name as string);
	}
}
